/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, ErrorHandler } from '@angular/core';
import { UpgradeModule } from '@angular/upgrade/static';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { environment } from 'src/environments/environment-variables';

import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireDatabaseModule } from '@angular/fire/compat/database';
import { AngularFireStorageModule } from '@angular/fire/compat/storage';

import { InitEarly } from 'src/app/services/InitEarly';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { EmptyComponent } from './components/routes/empty.component';
import { LoadingComponent } from './components/loading-component/loading.component';
import { UtilityComponentsModule } from './common/utility-components/utility-components.module';
import { TicketEditModule } from 'src/app/modules/ticket-edit/ticket-edit.module';
import { TicketEditPanelModule } from './modules/ticket-edit-panel/ticket-edit-panel.module';
import { ApplicationWideMessageModule } from './common/application-wide-message/application-wide-message.module';

import { ReportBlobber } from './services/report-blobber.service';
import { PlanState } from 'src/app/services/plan-state.service';
import { ProjectState } from './services/project-state.service';
import { RouteUtils } from 'src/app/services/route-utils.service';
import { ProjectNotifier } from 'src/app/services/project-notifier.service';
import { TicketCreatorService } from './services/ticket-creator.service';
import { ValidatedPlanActions } from './services/validated-plan-actions.service';
import { ProjectSchedulingModule } from './modules/project-scheduling/project-scheduling.module';
import { AngularExporter } from './services/angular-exporter.service';

import { ReloadMonitoring } from './services/background-scripts/reload-monitoring.service';
import { CanvasViewModule } from 'src/app/modules/canvas-view/canvas-view.module';
import { ZoomControlModule } from './common/zoom-control/zoom-control.module';

import { UpgradeShorthandModule } from './modules/plan/upgrade-shorthand.module';
import { PlanModule } from './modules/plan/plan.module';
import { PopupModule } from './common/popup';
import { UndoModule } from './common/undo/undo.module';
import { AjsUpgradeModule } from 'src/app/services/legacy-helpers/ajs-upgrade-providers';

import { AnotherErrorHandler } from './services/AnotherErrorHandler';
import { ColorPickerModule } from './common/color-picker/color-picker.module';
import { IntercomService } from './services/intercom.service';
import { ProcoreService } from './services/procore.service';
import { OrdinalCalculatorFactory } from './services/OrdinalCalculatorFactory.service';
import { EnterpriseDashboardModule } from './modules/enterprise-dashboard/enterprise-dashboard.module';

import { PositionManagerService } from './services/position-manager.service';

import { FeatureFlagManager } from './services/FeatureFlagManager';
import { OverviewProjectService } from './services/overview-project.service';

import { CookieService } from 'ngx-cookie-service';

import {
	PlanComponent,
	AccountComponent,
	HarnessComponent,
	Harness2Component,
	ZoneDebugComponent,
} from './components/routes';
import { PlanControllerUpgradeDirective } from '@appLegacy/components';
import { masterFancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { CustomFieldModule } from './modules/custom-field/custom-field.module';
import { OrganizationsModule } from './modules/organizations/organizations.module';
import { PlatformConfiguration } from './services/platform-configuration/platform-configuration.service';
import { PlatformDesktopConfiguration } from './services/platform-configuration/platform-desktop-configuration.service';
import { SharedUtilityComponentsModule } from './common/utility-components/shared-utility-components.module';
import { ReportsModule } from './modules/reports/reports.module';
import { SharedUIMaterialModule } from 'src/app/shared/ui/material/shared-ui-material.module';
import { DockedTicketModule } from './common/docked-ticket/docked-ticket.module';
import { DependencyDeletionComponent } from './components/popups/dependency-deletion/dependency-deletion.component';
import { TouchplanRestInterceptor } from './services/rest-api-configuration/touchplan-rest-interceptor';

import { MaterialModule } from 'src/app/common/material/material-module';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { provideDatabase, getDatabase } from '@angular/fire/database';
import { connectFunctionsEmulator, getFunctions, provideFunctions } from '@angular/fire/functions';

import { RallyPointModule } from './common/rally-point';
import { ProcoreInterceptor } from './services/rest-api-configuration/procore-interceptor';
import { PlanTicketsFilterComponent } from './modules/plan-tickets-filter/components/plan-tickets-filter/plan-tickets-filter.component';
import { TicketListModule } from '../../projects/mobile/src/app/ticket-list/ticket-list.module';
import { MultiPanelModule } from './components/multi-panel/multi-panel.module';
import { FilteredTicketListPanelComponent } from './modules/plan-tickets-filter/components/filtered-ticket-list-panel/filtered-ticket-list-panel.component';
import { AutomationIdDirective } from './shared/directives/automation-id/automation-id.directive';
import { PlanFilterPanelComponent } from './components/routes/plan.component/plan-filter-panel/plan-filter-panel.component';
import { fancyDatabase } from '@touchplan/client-firebase';
import { TimelineTicketOverflowComponent } from './modules/plan/timeline-ticket-overflow/timeline-ticket-overflow.component';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { ISO8601_DATE_FORMATS, Iso8601DateAdapter } from './services/Iso8601DateAdapter';
import { PromiseNowButtonComponent } from './components/promise-now-button/promise-now-button.component';
import { PlanPrintPanelComponent } from './components/routes/plan.component/plan-print-panel/plan-print-panel.component';

@NgModule({
	declarations: [
		AppComponent,
		EmptyComponent,
		PlanComponent,
		AccountComponent,
		HarnessComponent,
		Harness2Component,
		LoadingComponent,

		//angular upgrades
		PlanControllerUpgradeDirective,
		DependencyDeletionComponent,
		ZoneDebugComponent,
	],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		AppRoutingModule,
		UpgradeModule,
		AngularFireModule.initializeApp(environment.firebase),
		AngularFireDatabaseModule,
		AngularFireAuthModule,
		AngularFireStorageModule,
		AutomationIdDirective,
		HttpClientModule,
		ReactiveFormsModule,
		CustomFieldModule,
		ZoomControlModule,
		TicketEditModule,
		TicketEditPanelModule,
		CanvasViewModule,
		AjsUpgradeModule,
		UpgradeShorthandModule,
		PlanModule,
		ColorPickerModule,
		UtilityComponentsModule,
		RallyPointModule,
		UndoModule,
		EnterpriseDashboardModule,
		PopupModule,
		ApplicationWideMessageModule,
		ProjectSchedulingModule,
		OrganizationsModule,
		SharedUtilityComponentsModule,
		ReportsModule,
		SharedUIMaterialModule,
		DockedTicketModule,
		MaterialModule,
		PlanTicketsFilterComponent,
		MultiPanelModule,
		FilteredTicketListPanelComponent,
		TicketListModule,
		PlanFilterPanelComponent,
		PlanPrintPanelComponent,
		TimelineTicketOverflowComponent,
		PromiseNowButtonComponent,
	],
	providers: [
		InitEarly,
		PlanState,
		ProjectState,
		RouteUtils,
		ProjectNotifier,
		ReportBlobber,
		AnotherErrorHandler,
		AngularExporter,
		{ provide: ErrorHandler, useClass: AnotherErrorHandler },
		{ provide: PlatformConfiguration, useClass: PlatformDesktopConfiguration },
		IntercomService,
		ProcoreService,
		ValidatedPlanActions,
		TicketCreatorService,
		ReloadMonitoring,
		PositionManagerService,
		FeatureFlagManager,
		OverviewProjectService,
		OrdinalCalculatorFactory,
		CookieService,
		{ provide: HTTP_INTERCEPTORS, useClass: TouchplanRestInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ProcoreInterceptor, multi: true },
		{ provide: DateAdapter, useClass: Iso8601DateAdapter },
		{ provide: MAT_DATE_FORMATS, useValue: ISO8601_DATE_FORMATS },
		//angularfire modular api initialization, compat and modular need to be initialized seperately
		//note that only the app, auth, db and functions are initialized
		//angular/fire v17 returns EnvironmentProviders, so these belong in the providers array, not imports.
		provideFirebaseApp(() => initializeApp(environment.firebase)),
		provideAuth(() => getAuth()),
		provideDatabase(() => getDatabase()),
		provideFunctions(() => {
			{
				const functions = getFunctions();
				if (environment.useFirebaseFunctionsEmulator) {
					connectFunctionsEmulator(functions, 'localhost', 5001);
				}
				return functions;
			}
		}),
	],
})
export class AppModule {
	constructor(private upgrade: UpgradeModule, initEarly: InitEarly) {}
	public ngDoBootstrap() {
		//hack to make angularfire1 not make a mess
		window.angularZone = Zone.current;

		//as early as possible...
		masterFancyFirebaseReferencer.masterInit(
			{
				firebaseRefUrl: environment.refUrl,
				firebaseDatabaseUrl: environment.firebase.databaseURL,
				enablePartitioning: true,
			},
			fancyDatabase
		);

		this.upgrade.bootstrap(document.body, ['ticketspaceApp'], { strictDi: true });
	}
}
